import React from 'react';
import { AlertTriangleIcon, CheckIcon, UndoIcon } from 'lucide-react';
import { HotLead } from '../../types/hotLead';
import { formatCurrency, formatShortDate } from '../../utils/format';
import { OverrideForm } from './OverrideForm';
import { ReviewBadge } from './ReviewBadge';
interface HotLeadRowProps {
  lead: HotLead;
  isOverriding: boolean;
  onStartOverride: () => void;
  onCancelOverride: () => void;
  onAgree: () => void;
  onOverride: (reasonCode: string, reasonSubcode: string) => void;
}
const signalLabels: Array<{
  key: keyof HotLead['signals'];
  label: string;
}> = [{
  key: 'rateThreshold',
  label: 'Rate'
}, {
  key: 'equity',
  label: 'Equity'
}, {
  key: 'meaningfulConversation',
  label: 'Conversation'
}];
export function HotLeadRow({
  lead,
  isOverriding,
  onStartOverride,
  onCancelOverride,
  onAgree,
  onOverride
}: HotLeadRowProps) {
  const isPending = lead.review.status === 'pending';
  return <li className="border-b border-slate-200 last:border-b-0" data-id="element-54" data-mp="186fsf3p09pq2">
      <article className="px-4 py-3" data-id="element-55" data-mp="cgcncxt4c6ir">
        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-56" data-mp="che5fm8jxt9z">
          <div className="min-w-0" data-id="element-57" data-mp="7h8fqblueguj">
            <div className="flex items-center gap-2" data-id="element-58" data-mp="7g6vbgixsi05">
              {lead.urgent && <span className="inline-flex items-center gap-1 rounded-full bg-[#ba0517] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white" data-id="element-59" data-mp="3gg6hicz0by9">
                  <AlertTriangleIcon className="h-3 w-3" aria-hidden="true" data-id="element-60" data-mp="v7c97q5bh07t" />
                  Urgent
                </span>}
              <h3 className="truncate text-sm font-semibold text-[#0176d3]" data-id="element-61" data-mp="r6aixytdnyhz">
                {lead.name}
              </h3>
              <span className="text-xs text-slate-500" data-id="element-62" data-mp="mcfruabg0h8v">{lead.id}</span>
            </div>

            <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-3 lg:grid-cols-6" data-id="element-63" data-mp="hdl9talauwpt">
              <Field label="Loan amount" value={formatCurrency(lead.loanAmount)} data-id="element-64" data-mp="1ewwh8ydky4es" />
              <Field label="Seasoning" value={formatShortDate(lead.seasoningDate)} data-id="element-65" data-mp="1a31vzqibthdl" />
              <Field label="Loan type" value={lead.loanType} data-id="element-66" data-mp="1t154w28mpnmw" />
              <Field label="Credit pull" value={formatShortDate(lead.creditPullDate)} data-id="element-67" data-mp="1y083p4y0mxmf" />
              <Field label="Stage" value={lead.stage} data-id="element-68" data-mp="1k8jmwoctnkz4" />
              <Field label="Disposition" value={lead.disposition} data-id="element-69" data-mp="1k81lzef7sikv" />
            </dl>

            <div className="mt-2 flex flex-wrap items-center gap-1.5" data-id="element-70" data-mp="1o8c1pist6ncx">
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700" data-id="element-71" data-mp="1a9uh3ty79qz8">
                Score {lead.score}
              </span>
              {signalLabels.map(({
              key,
              label
            }) => <span key={key} className={lead.signals[key] ? 'rounded border border-[#acf3e4] bg-[#ebfdf7] px-1.5 py-0.5 text-[11px] font-medium text-[#056764]' : 'rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-400 line-through'} data-id="element-72" data-mp="11aqwgljdx3im">
                  {label}
                </span>)}
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600" data-id="element-73" data-mp="k0wi8n4m3427">
                Est. value {formatCurrency(lead.estimatedValue)}
              </span>
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600" data-id="element-74" data-mp="t11o2su5pue9">
                {lead.touches.length} touches
              </span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2" data-id="element-75" data-mp="12quz9sqe07hz">
            {isPending ? <>
                <button type="button" onClick={onAgree} className="inline-flex items-center gap-1 rounded bg-[#0176d3] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#014486] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-1" data-id="element-76" data-mp="1gqw0xaeh7iyi">
                  <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-77" data-mp="3dkqs7vrheng8" />
                  Agree
                </button>
                <button type="button" onClick={onStartOverride} className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-78" data-mp="3jywcjjg9hioy">
                  <UndoIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-79" data-mp="2wawoirac46e2" />
                  Override
                </button>
              </> : <ReviewBadge review={lead.review} showReason data-id="element-80" data-mp="366zoprlmluyd" />}
          </div>
        </div>
      </article>

      {isOverriding && <OverrideForm leadName={lead.name} onCancel={onCancelOverride} onSubmit={onOverride} data-id="element-81" data-mp="3azsv0jwqtbck" />}
    </li>;
}
function Field({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="min-w-0" data-id="element-82" data-mp="2aye5cw9yxk5y">
      <dt className="text-[11px] uppercase tracking-wide text-slate-500" data-id="element-83" data-mp="2ayo00x5ykfhz">{label}</dt>
      <dd className="truncate font-medium text-slate-900" data-id="element-84" data-mp="25yrv8vpfsk1d">{value}</dd>
    </div>;
}